import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Address, AddressService } from 'api';
import {
  BehaviorSubject,
  ReplaySubject,
  catchError,
  filter,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ButtonComponent } from 'ui-elements/button';
import { ModalComponent } from 'ui-elements/modal';
import { RadioCardModule } from 'ui-elements/form/radio-card';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { AddressPipe } from 'common';
import { isEqual } from 'lodash-es';

@Component({
  selector: 'ss-www-verify-address-modal',
  imports: [
    CommonModule,
    RadioCardModule,
    ModalComponent,
    ReactiveFormsModule,
    FormsModule,
    AddressPipe,
    ButtonComponent,
  ],
  templateUrl: './verify-address-modal.component.html',
  styleUrl: './verify-address-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VerifyAddressModalComponent {
  private addressService = inject(AddressService);

  address$ = new BehaviorSubject<Address | null>(null);
  verifiedAddress = toSignal(
    this.address$.pipe(
      filter((address) => !!address),
      switchMap((address) =>
        this.addressService
          .verifyAddress(address!)
          .pipe(catchError(() => of([address!])))
      ),
      map((addresses) => addresses[0]),
      tap((address) => {
        if (this.checkIfAddressesMatch(address, this.address$.value!)) {
          this.selectedAddress$.next(address);
        }
      })
    )
  );
  selectedAddress$ = new ReplaySubject<Address>(1);
  selectAddressFormControl = new FormGroup({
    selectedAddress: new FormControl<'SUGGESTED' | 'ENTERED' | null>(
      'SUGGESTED'
    ),
  });

  onSubmit() {
    if (this.selectAddressFormControl.value.selectedAddress === 'SUGGESTED') {
      this.selectedAddress$.next(this.verifiedAddress()!);
    } else {
      this.selectedAddress$.next(this.address$.value!);
    }
  }

  private checkIfAddressesMatch(address1: Address, address2: Address) {
    return isEqual(
      this.makeAllValuesUppercaseAndRemoveEmpty(address1),
      this.makeAllValuesUppercaseAndRemoveEmpty(address2)
    );
  }

  private makeAllValuesUppercaseAndRemoveEmpty(address: Address) {
    return Object.fromEntries(
      Object.entries(address)
        .map(([key, value]) => [key, value?.toUpperCase()?.trim()])
        .filter(([_, value]) => !!value)
    );
  }
}
